import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import salesContinuousServiceService from '../../../services/salesContinuousServiceService';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import CloseServiceModal from './CloseServiceModal';
import Toast from '../../../components/Common/Toast';
import LazyTableWithMonthGroups from '../../../components/Common/LazyTableWithMonthGroups';
import { useLazyMonthList } from '../../../hooks/useLazyMonthList';
import MutateGate from '../../../components/Common/MutateGate';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import { POS_CONFIRMED_STATE } from '../../../utils/ventasPosNavigation';
import SalesContinuousServicePdfButton from '../../../components/Ventas/SalesContinuousServicePdfButton';
import SalesContinuousServicePrintButton from '../../../components/Ventas/SalesContinuousServicePrintButton';
import ListCurrencyBadge from '../../../components/Common/ListCurrencyBadge';
import { formatMoneyInDocumentCurrency } from '../../../utils/documentCurrency';

const VentasContinuousServicesIndex = () => {
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [serviceToClose, setServiceToClose] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const buildListParams = useCallback(() => {
    if (!selectedPointOfSale?.id) return {};
    const params = { point_of_sale_id: selectedPointOfSale.id };
    if (statusFilter !== 'all') params.status = statusFilter;
    const searchParts = [clientSearch.trim(), searchTerm.trim()].filter(Boolean);
    if (searchParts.length) params.search = searchParts.join(' ');
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [selectedPointOfSale?.id, statusFilter, clientSearch, searchTerm, dateFrom, dateTo]);

  const fetchSummary = useCallback(
    (params) => {
      if (!params.point_of_sale_id) return Promise.resolve({ success: true, data: { months: [], stats: null } });
      return salesContinuousServiceService.getContinuousServices({ ...params, month_summary: 1 });
    },
    []
  );
  const fetchMonth = useCallback((params) => salesContinuousServiceService.getContinuousServices(params), []);

  const {
    monthSummaries,
    loadingSummary,
    loadMonthDetail,
    getMonthItems,
    isMonthLoading,
    reloadSummary,
  } = useLazyMonthList({
    buildParams: buildListParams,
    fetchSummary,
    fetchMonth,
    searchTerm: `${clientSearch} ${searchTerm}`,
  });

  const handleCloseClick = (service) => {
    setServiceToClose(service);
    setIsCloseModalOpen(true);
  };

  const handleCloseConfirm = async () => {
    if (!serviceToClose) return;
    setLoading(true);
    try {
      const response = await salesContinuousServiceService.closeContinuousService(serviceToClose.id);
      if (response.success) {
        setToast({ isOpen: true, message: 'Servicio continuo cerrado', type: 'success' });
        setIsCloseModalOpen(false);
        setServiceToClose(null);
        reloadSummary();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cerrar', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cerrar el servicio continuo', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config =
      status === 'open'
        ? { label: 'Abierto', className: 'bg-green-100 text-green-800' }
        : { label: 'Cerrado', className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatRowAmount = (amount, currency) => formatMoneyInDocumentCurrency(amount, currency);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Servicios continuos (ventas)</h1>
          <p className="text-sm text-gray-600 mt-1">Contratos y facturación recurrente de ventas</p>
        </div>
        <MutateGate>
          <Link
            to="/ventas/continuous-services/create"
            state={POS_CONFIRMED_STATE}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
          >
            <span>+</span>
            Nuevo servicio continuo
          </Link>
        </MutateGate>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Código..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder="Buscar por nombre de cliente..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos</option>
              <option value="open">Abierto</option>
              <option value="closed">Cerrado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {!selectedPointOfSale?.id ? (
          <div className="p-8 text-center text-gray-500">Selecciona un punto de venta</div>
        ) : loadingSummary && monthSummaries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Cargando resumen por mes…</div>
        ) : monthSummaries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No se encontraron servicios continuos</p>
            <Link
              to="/ventas/continuous-services/create"
              state={POS_CONFIRMED_STATE}
              className="mt-4 inline-block text-teal-600 hover:text-teal-700 font-medium"
            >
              Crear el primero
            </Link>
          </div>
        ) : (
          <LazyTableWithMonthGroups
            months={monthSummaries}
            getMonthItems={getMonthItems}
            isMonthLoading={isMonthLoading}
            onMonthExpand={loadMonthDetail}
            defaultCollapsed
            columnCount={10}
            emptyMessage="No se encontraron servicios continuos"
            headerRow={
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha inicio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mon.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha fin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base imponible</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGV</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto acumulado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            }
            renderRow={(service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to={`/ventas/continuous-services/${service.id}`}
                    state={POS_CONFIRMED_STATE}
                    className="text-teal-600 hover:text-teal-800 font-medium"
                  >
                    {service.code || `SCV-${service.id}`}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {service.client?.name || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {service.start_date ? formatDateLocaleLima(service.start_date) : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ListCurrencyBadge currency={service.currency} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {service.end_date ? formatDateLocaleLima(service.end_date) : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {formatRowAmount(service.base_imponible, service.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {formatRowAmount(service.igv_amount, service.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {formatRowAmount(service.total_amount, service.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(service.status)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end items-center gap-2">
                    <SalesContinuousServicePrintButton serviceId={service.id} />
                    <SalesContinuousServicePdfButton
                      serviceId={service.id}
                      onSuccess={(s) =>
                        setToast({
                          isOpen: true,
                          message: `PDF de ${s.code} descargado`,
                          type: 'success',
                        })
                      }
                      onError={(message) =>
                        setToast({
                          isOpen: true,
                          message: message || 'Error al generar el PDF',
                          type: 'error',
                        })
                      }
                    />
                    <Link
                      to={`/ventas/continuous-services/${service.id}`}
                      state={POS_CONFIRMED_STATE}
                      className="text-teal-600 hover:text-teal-900"
                    >
                      Ver
                    </Link>
                    {service.status === 'open' && (
                      <MutateGate>
                        <button
                          type="button"
                          onClick={() => handleCloseClick(service)}
                          className="text-orange-600 hover:text-orange-900"
                        >
                          Cerrar
                        </button>
                      </MutateGate>
                    )}
                  </div>
                </td>
              </tr>
            )}
          />
        )}
      </div>

      <CloseServiceModal
        isOpen={isCloseModalOpen}
        onClose={() => {
          setIsCloseModalOpen(false);
          setServiceToClose(null);
        }}
        onConfirm={handleCloseConfirm}
        serviceName={serviceToClose?.code || (serviceToClose ? `SCV-${serviceToClose.id}` : '')}
        loading={loading}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default VentasContinuousServicesIndex;
